import React from 'react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { getCurrentPartnerName } from '../../lib/auth';

/**
 * Canonical "Paid By" selector used across all transaction-entry and
 * refund drawers in the platform.
 *
 *   • BillPortal           — paid out of BillPortal's bank account
 *   • {partnerName}        — paid by the org's partner (Anchor / BP-US / …)
 *   • Customer             — paid directly by the customer org
 *   • Previous Incumbent   — paid by the legacy supplier the org is moving off
 *
 * STORAGE NOTE: the value stored in DB stays the literal partner name
 * (e.g. "Anchor" or "Bill Portal-US") so existing aggregations work.
 * After PARTNER_DYNAMIC_LABEL_SPEC Phase 2 lands, backend writes also
 * stamp `paid_by_kind` (`"partner"`) which is the canonical join key.
 *
 * Props:
 *   value           — current stored paid_by_name string
 *   onChange(value) — emitted with the stored string
 *   partnerName     — display name of the org's partner. If undefined,
 *                     auto-resolves via getCurrentPartnerName(). Pass
 *                     `null` explicitly to HIDE the partner option (for
 *                     orgs with no partner).
 *   customerLabel   — org name shown after "Customer"
 */
export const PAID_BY_OPTIONS = ['BillPortal', 'Anchor', 'Customer', 'Previous Incumbent'];

export const PaidBySelect = ({
  value,
  onChange,
  partnerName,
  customerLabel,
  className = 'h-9 text-sm',
  testId = 'paid-by-select',
  disabled = false,
}) => {
  // Resolve partner name with three-state semantics:
  //   undefined  → auto-resolve from current account (fallback for callers
  //                that haven't yet been updated to pass it explicitly)
  //   null       → explicitly hide the partner row (no-partner orgs)
  //   string     → use as the display label, store as paid_by_name
  const resolvedPartner = partnerName === undefined ? getCurrentPartnerName() : partnerName;
  const showPartnerRow = resolvedPartner !== null && resolvedPartner !== '';
  // The DB-stored value for the partner row. Defaults to "Anchor" for
  // historical compatibility when the org has a partner but we couldn't
  // resolve the name.
  const partnerStoredValue = resolvedPartner || 'Anchor';
  const partnerDisplay = resolvedPartner || 'Anchor';

  // Build the option list so we can include the live partner value
  // even when it isn't one of the canonical strings (e.g. "Bill Portal-US").
  const v = value || 'BillPortal';

  return (
    <Select value={v} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className={className} data-testid={testId}>
        <SelectValue placeholder="Select…" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="BillPortal" data-testid={`${testId}-billportal`}>BillPortal</SelectItem>
        {showPartnerRow && (
          <SelectItem value={partnerStoredValue} data-testid={`${testId}-partner`}>
            {partnerDisplay} (Partner)
          </SelectItem>
        )}
        <SelectItem value="Customer" data-testid={`${testId}-customer`}>
          Customer{customerLabel ? ` (${customerLabel})` : ''}
        </SelectItem>
        <SelectItem value="Previous Incumbent" data-testid={`${testId}-previous-incumbent`}>
          Previous Incumbent
        </SelectItem>
        {/* If the persisted value is a partner string we don't already
            render (e.g. legacy "Anchor" on a BP-US org), surface it so
            the field stays valid. */}
        {value && !['BillPortal', partnerStoredValue, 'Customer', 'Previous Incumbent'].includes(value) && (
          <SelectItem value={value} data-testid={`${testId}-legacy`}>
            {value} (legacy)
          </SelectItem>
        )}
      </SelectContent>
    </Select>
  );
};

export default PaidBySelect;
